import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Compartment, EditorState } from "@codemirror/state";
import { EditorView, drawSelection, keymap, placeholder as cmPlaceholder, dropCursor } from "@codemirror/view";
import { defaultKeymap } from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { HighlightStyle, syntaxHighlighting, defaultHighlightStyle, bracketMatching } from "@codemirror/language";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { tags as t } from "@lezer/highlight";
import * as Y from "yjs";
import { yCollab, yUndoManagerKeymap } from "y-codemirror.next";
import { blockEndAt, blockInsertion } from "@brainlog/shared";
import type { CollabEntry } from "./collab";
import { livePreview, refreshCitations, refreshFigures, type FigureContext, type OpenLinkHandler } from "./livePreview";
import { citeCompletion, type CiteSource } from "./citeComplete";
import { citeHover } from "./citeHover";
import { commentMarks, setCommentMarks, type CommentHandlers, type CommentMark } from "./commentMarks";

export interface EditorHandle {
  insertAtCursor(text: string): void;
  /** Puts a block (a figure's line) on its own, between blank lines: after the paragraph with the cursor, or at the end. */
  insertBlock(text: string, where: "cursor" | "end"): void;
  getSelection(): { text: string; from: number; to: number };
  focus(): void;
  /** Where a line (1-based) starts on screen, in viewport pixels; null before the editor exists. */
  lineTop(line: number): number | null;
}

// Heading sizes come from the line styles of the live rendering (cm-md-h1…).
const mdHighlight = HighlightStyle.define([
  { tag: [t.heading1, t.heading2, t.heading3, t.heading4, t.heading5, t.heading6], fontWeight: "inherit" },
  { tag: t.strong, fontWeight: "700" },
  { tag: t.emphasis, fontStyle: "italic" },
  { tag: t.strikethrough, textDecoration: "line-through" },
  { tag: t.link, color: "inherit" },
  { tag: t.url, color: "var(--muted-foreground)" },
  { tag: t.monospace, fontFamily: "var(--font-mono)", fontSize: "0.9em" },
  { tag: t.quote, color: "inherit" },
  { tag: t.comment, color: "var(--muted-foreground)", fontStyle: "italic" },
  { tag: [t.processingInstruction, t.meta], color: "var(--muted-foreground)" },
]);

/** Wraps the selection in a Markdown marker (Ctrl/Cmd+B, Ctrl/Cmd+I), or removes it when already wrapped. */
function toggleMarker(marker: string) {
  return (view: EditorView) => {
    if (view.state.readOnly) return false;
    const r = view.state.selection.main;
    const n = marker.length;
    const before = view.state.sliceDoc(r.from - n, r.from);
    const after = view.state.sliceDoc(r.to, r.to + n);
    if (before === marker && after === marker) {
      view.dispatch({ changes: [{ from: r.from - n, to: r.from }, { from: r.to, to: r.to + n }], selection: { anchor: r.from - n, head: r.to - n } });
    } else {
      view.dispatch({ changes: [{ from: r.from, insert: marker }, { from: r.to, insert: marker }], selection: { anchor: r.from + n, head: r.to + n } });
    }
    return true;
  };
}

export const MarkdownEditor = forwardRef<
  EditorHandle,
  {
    entry: CollabEntry;
    readOnly?: boolean;
    onSelectionChange?(sel: { text: string; from: number; to: number }): void;
    /** An internal link (bl://…) was clicked, with the text around it. */
    onOpenLink?: OpenLinkHandler;
    /** Papers to cite with \cite{ (the notebook's or project's library). */
    cite?: CiteSource;
    /** Comments on passages: highlighted in the text; selecting text offers "Comment". */
    comments?: { marks: CommentMark[] } & CommentHandlers;
    /** Figures: where their numbers start (manuscript sections) and the project their images must belong to. */
    figures?: FigureContext;
    ariaLabel?: string;
    placeholder?: string;
  }
>(function MarkdownEditor({ entry, readOnly, onSelectionChange, onOpenLink, cite, comments, figures, ariaLabel, placeholder }, ref) {
  const host = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const selCb = useRef(onSelectionChange);
  selCb.current = onSelectionChange;
  const linkCb = useRef(onOpenLink);
  linkCb.current = onOpenLink;
  const citeRef = useRef(cite);
  citeRef.current = cite;
  const commentRef = useRef<CommentHandlers | undefined>(comments);
  commentRef.current = comments;
  const figuresRef = useRef(figures);
  figuresRef.current = figures;
  const hasComments = !!comments;
  // Changed in place, never by rebuilding the editor: a rebuild (for example when the title is
  // renamed) would take the focus away and drop what is being typed at that moment.
  const dynamic = useMemo(() => ({ label: new Compartment(), editable: new Compartment(), comments: new Compartment() }), []);
  const labelExt = (label?: string) => EditorView.contentAttributes.of({ "aria-label": label ?? "Note text", "aria-multiline": "true" });
  const editableExt = (ro?: boolean) => [EditorState.readOnly.of(!!ro), EditorView.editable.of(!ro)];
  const commentsExt = (on: boolean) => (on ? [commentMarks(commentRef)] : []);

  useEffect(() => {
    if (!host.current) return;
    const ytext = entry.doc.getText("content");
    const undoManager = new Y.UndoManager(ytext);
    const view = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: ytext.toString(),
        extensions: [
          keymap.of([{ key: "Mod-b", run: toggleMarker("**") }, { key: "Mod-i", run: toggleMarker("*") }, ...yUndoManagerKeymap, ...defaultKeymap, ...searchKeymap]),
          drawSelection(),
          dropCursor(),
          highlightSelectionMatches(),
          bracketMatching(),
          markdown({ base: markdownLanguage, codeLanguages: languages }),
          syntaxHighlighting(mdHighlight),
          syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
          EditorView.lineWrapping,
          livePreview(linkCb, citeRef, figuresRef),
          citeCompletion(citeRef),
          citeHover(linkCb),
          dynamic.comments.of(commentsExt(hasComments)),
          cmPlaceholder(placeholder ?? "Start writing… Type # and a space for a title, - for a list, > for a quote, \\cite{ to cite a paper."),
          dynamic.label.of(labelExt(ariaLabel)),
          dynamic.editable.of(editableExt(readOnly)),
          yCollab(ytext, entry.awareness, { undoManager }),
          EditorView.updateListener.of((u) => {
            if (u.selectionSet || u.docChanged) {
              const r = u.state.selection.main;
              selCb.current?.({ text: u.state.sliceDoc(r.from, r.to), from: r.from, to: r.to });
            }
          }),
        ],
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      undoManager.destroy();
      viewRef.current = null;
    };
  }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    viewRef.current?.dispatch({ effects: dynamic.label.reconfigure(labelExt(ariaLabel)) });
  }, [ariaLabel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    viewRef.current?.dispatch({ effects: dynamic.editable.reconfigure(editableExt(readOnly)) });
  }, [readOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    viewRef.current?.dispatch({ effects: dynamic.comments.reconfigure(commentsExt(hasComments)) });
  }, [hasComments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Comments loaded or changed: redraw the highlights.
  const marksKey = JSON.stringify(comments?.marks.map((m) => [m.id, m.quote]) ?? []);
  useEffect(() => {
    viewRef.current?.dispatch({ effects: setCommentMarks.of(comments?.marks ?? []) });
  }, [marksKey, entry, hasComments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sections before this one gained or lost figures: renumber.
  useEffect(() => {
    viewRef.current?.dispatch({ effects: refreshFigures.of(null) });
  }, [figures?.start, figures?.workspaceId]);

  // The library loaded or changed: redraw "(Author, Year)" labels.
  useEffect(() => {
    viewRef.current?.dispatch({ effects: refreshCitations.of(null) });
  }, [cite?.version]);

  useImperativeHandle(ref, () => ({
    insertAtCursor(text: string) {
      const v = viewRef.current;
      if (!v) return;
      const r = v.state.selection.main;
      v.dispatch({ changes: { from: r.from, to: r.to, insert: text }, selection: { anchor: r.from + text.length } });
      v.focus();
    },
    insertBlock(text: string, where: "cursor" | "end") {
      const v = viewRef.current;
      if (!v) return;
      const doc = v.state.doc.toString();
      const c = blockInsertion(doc, where === "end" ? doc.length : blockEndAt(doc, v.state.selection.main.head), text);
      v.dispatch({ changes: c, selection: { anchor: c.from + c.insert.length }, scrollIntoView: true });
    },
    getSelection() {
      const v = viewRef.current;
      if (!v) return { text: "", from: 0, to: 0 };
      const r = v.state.selection.main;
      return { text: v.state.sliceDoc(r.from, r.to), from: r.from, to: r.to };
    },
    focus() {
      viewRef.current?.focus();
    },
    lineTop(line: number) {
      const v = viewRef.current;
      if (!v) return null;
      const n = Math.min(Math.max(1, line), v.state.doc.lines);
      return v.documentTop + v.lineBlockAt(v.state.doc.line(n).from).top;
    },
  }));

  return <div ref={host} className="cm-host" />;
});
